import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchStatusMetadata } from "model/StatusMetadataModel";
import {
  buildStatusMetadataIndex,
  getStatusMetadataDisplayLabel,
  getStatusMetadataInlineStyle,
  lookupStatusMetadataItem,
  resolveStatusMetadataGroupKey,
} from "helpers/statusMetadataHelpers";
import {
  registerStatusMetadataLoader,
  registerStatusBadgeResolver,
} from "libs/context/statusMetadataBridge";
import {
  clearStatusMetadataSessionCache,
  getEmptyStatusMetadataIndex,
  getStatusMetadataSessionIndex,
  getStatusMetadataSessionLoadPromise,
  setStatusMetadataSessionIndex,
  setStatusMetadataSessionLoadPromise,
} from "libs/context/statusMetadataSessionCache";
import {
  statusType,
  quotaType,
  incentiveType,
  normalizeYesNoDisplayLabel,
} from "helpers/GeneralHelper";

const StatusMetadataContext = createContext(null);

const resolveFallbackClassName = (accessor, pageName, value, explicitFallback = "") => {
  if (explicitFallback) return explicitFallback;

  const acc = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  const normalizedValue = String(value ?? "").trim();

  if (acc === "incentive") return incentiveType(normalizedValue);
  if (
    [
      "quota",
      "forclient",
      "forjob",
      "fortools",
      "candidatedeactive",
      "isallowed",
      "approved",
      "online",
      "blocked",
      "isregister",
      "teamrequired",
      "uniqueclient",
      "multipleteam",
      "isbackend",
      "isforuscitizens",
      "isfornonuscitizens",
      "isdefault",
    ].includes(acc)
  ) {
    const yesNo =
      value === true || value === "1" || normalizedValue.toLowerCase() === "yes"
        ? "Yes"
        : "No";
    return quotaType(yesNo);
  }

  if (normalizedValue.toLowerCase() === "red") return statusType("red");
  if (normalizedValue.toLowerCase() === "yellow") return statusType("yellow");
  if (normalizedValue.toLowerCase() === "green") return statusType("success");

  return statusType(normalizedValue);
};

export const StatusMetadataProvider = ({ children }) => {
  /** Bumped once when metadata loads so badges can pick up API colors (single re-render wave). */
  const [metadataVersion, setMetadataVersion] = useState(() =>
    getStatusMetadataSessionIndex() ? 1 : 0
  );

  const indexRef = useRef(getStatusMetadataSessionIndex() ?? getEmptyStatusMetadataIndex());

  const applySessionIndex = useCallback((nextIndex) => {
    indexRef.current = nextIndex;
    setStatusMetadataSessionIndex(nextIndex);
    setMetadataVersion((version) => version + 1);
  }, []);

  const loadStatusMetadata = useCallback(async ({ force = false } = {}) => {
    if (!localStorage.getItem("authUser")) {
      clearStatusMetadataSessionCache();
      indexRef.current = getEmptyStatusMetadataIndex();
      setMetadataVersion(0);
      return null;
    }

    if (!force && getStatusMetadataSessionIndex()) {
      indexRef.current = getStatusMetadataSessionIndex();
      return null;
    }

    const inflight = getStatusMetadataSessionLoadPromise();
    if (inflight) {
      return inflight;
    }

    const promise = fetchStatusMetadata()
      .then((response) => {
        const items = response?.data?.items ?? response?.items ?? [];
        if (response?.success !== false && Array.isArray(items)) {
          applySessionIndex(buildStatusMetadataIndex(items));
        }
        return response;
      })
      .catch(() => null)
      .finally(() => {
        setStatusMetadataSessionLoadPromise(null);
      });

    setStatusMetadataSessionLoadPromise(promise);
    return promise;
  }, [applySessionIndex]);

  /** Stable — reads latest index from ref (avoids effect loop on every load). */
  const resolveBadgePresentation = useCallback(
    ({ accessor, pageName = "", value, fallbackClassName = "" }) => {
      const groupKey = resolveStatusMetadataGroupKey(accessor, pageName);
      const yesNoLabel =
        groupKey === "yes-no" ? normalizeYesNoDisplayLabel(value) : null;
      const resolvedValue = yesNoLabel ?? value;
      const trimmed = String(resolvedValue ?? "").trim();
      const item = lookupStatusMetadataItem(indexRef.current, groupKey, resolvedValue);
      const inlineStyle = getStatusMetadataInlineStyle(item);

      const displayLabel =
        getStatusMetadataDisplayLabel(item, resolvedValue) ||
        yesNoLabel ||
        (["yes", "no"].includes(trimmed.toLowerCase())
          ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
          : trimmed);

      return {
        item,
        groupKey,
        displayLabel,
        inlineStyle,
        fallbackClassName: inlineStyle
          ? ""
          : resolveFallbackClassName(
              accessor,
              pageName,
              resolvedValue,
              fallbackClassName || (yesNoLabel ? quotaType(yesNoLabel) : "")
            ),
      };
    },
    []
  );

  const loadRef = useRef(loadStatusMetadata);
  loadRef.current = loadStatusMetadata;
  const resolveRef = useRef(resolveBadgePresentation);
  resolveRef.current = resolveBadgePresentation;

  useEffect(() => {
    registerStatusMetadataLoader((options) => loadRef.current(options));
    registerStatusBadgeResolver((params) => resolveRef.current(params));

    if (localStorage.getItem("authUser")) {
      loadRef.current({ force: false });
    }

    return () => {
      registerStatusMetadataLoader(null);
      registerStatusBadgeResolver(null);
    };
  }, []);

  const value = useMemo(
    () => ({
      isReady: Boolean(getStatusMetadataSessionIndex()),
      metadataVersion,
      loadStatusMetadata,
      resolveBadgePresentation,
      lookupStatusMetadataItem: (accessor, pageName, rawValue) => {
        const groupKey = resolveStatusMetadataGroupKey(accessor, pageName);
        return lookupStatusMetadataItem(indexRef.current, groupKey, rawValue);
      },
    }),
    [metadataVersion, loadStatusMetadata, resolveBadgePresentation]
  );

  return (
    <StatusMetadataContext.Provider value={value}>{children}</StatusMetadataContext.Provider>
  );
};

export const useStatusMetadata = () => {
  const ctx = useContext(StatusMetadataContext);
  if (!ctx) {
    throw new Error("useStatusMetadata must be used within StatusMetadataProvider");
  }
  return ctx;
};
